import {getStore} from './store'

const BATCH_ACTION_TYPE = '@@BATCH'

/**
 * Current batch wrapper call depth.
 * @type {number}
 */
let depth = 0

/**
 * This array contains batch of real action objects, which are dispatched when batch wrapper ends execution.
 * @type {Array}
 */
let accumulatedActions = []

/**
 * Wraps any function enabling redux actions batching while function is executed.
 * @param f {Function}
 * @returns {Function|undefined}
 */
export function batched(f) {
    if (!f) {
        return
    }

    return function() {
        // Mark batched execution start.
        // Wrapped function may call another batch wrapper, each call increments counter more.
        depth++

        try {
            return f.apply(this, arguments)
        } finally {
            if (--depth === 0 && accumulatedActions.length > 0) {
                // All batched execution is completed, dispatch special action
                const payload = accumulatedActions
                accumulatedActions = []
                getStore().dispatch({type: BATCH_ACTION_TYPE, payload})
            }
        }
    }
}

/**
 * Wraps any promise enabling redux actions batching while resolving promises.
 * .then() and .catch() calls are wrapped as well as consecutive promises in the chain.
 *
 * Example:
 *
 * batchedPromise(somePromise)
 *     .then(() => {
 *         // batched actions are enabled here
 *     })
 *     .then(() => {
 *         // batched actions are enabled here too
 *     })
 *     .catch(() => {
 *         // batched actions are enabled here too
 *     })
 *
 * @returns {BatchPromiseWrapper}
 */
export function batchedPromise(p) {
    return new BatchPromiseWrapper(p)
}

class BatchPromiseWrapper {
    constructor(promise) {
        this.promise = promise
    }

    then(onResolve, onReject) {
        return new BatchPromiseWrapper(this.promise.then(batched(onResolve), batched(onReject)))
    }

    catch(onReject) {
        return new BatchPromiseWrapper(this.promise.catch(batched(onReject)))
    }
}

/**
 * Wraps reducer enabling support of batched actions.
 * @param reducer
 * @returns {Function}
 */
export function batchReducerWrapper(reducer) {
    return (state, action) => {
        if (action.type === BATCH_ACTION_TYPE) {
            // Call original reducer for every action in batch
            return action.payload.reduce(reducer, state)
        } else {
            // Pass regular actions directly into original reducer
            return reducer(state, action)
        }
    }
}

/**
 * Redux middleware for catching action objects and collecting them into batch.
 */
export function batchMiddleware() {
    return next => action => {
        if (depth > 0 && typeof action !== 'function') {
            accumulatedActions.push(action)
            return action
        }

        return next(action)
    }
}



// WEBPACK FOOTER //
// ./web/js/redux/batch.js